import React, { FC, useEffect, useState } from 'react';
import { Box, Button, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { User } from 'models/user';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type StartWorkDialogProps = {
    open: boolean;
    stepName?: string;
    title?: string;
    confirmLabel?: string;
    onConfirm: (userId: string) => void;
    onClose: () => void;
};

const StartWorkDialog: FC<StartWorkDialogProps> = ({ open, stepName, title, confirmLabel, onConfirm, onClose }) => {
    const { t: translate } = useTranslation(['common', 'workflow']);
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    // DD-3391: no external tier may read the lab's user list, so asking for it is a
    // hard ACCESS_DENIED. They are the technician on their own work anyway, so the
    // picker is seeded with them rather than left showing a raw id.
    const isExternalUser = permissions.isExternalTechnician(currentUser.role);
    const [users, setUsers] = useState<User[]>(isExternalUser ? [currentUser] : []);
    const [userId, setUserId] = useState(currentUser._id);

    useQuery(getTechniciansForUserListTemplate, {
        skip: isExternalUser,
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => setUsers(technicians),
        onError: (error) => showBoundary(error),
    });

    useEffect(() => {
        if (open) {
            setUserId(currentUser._id);
        }
    }, [open]);

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={title ?? translate('workflow:START_WORK')}
            actions={
                <>
                    <Button
                        variant="contained"
                        disabled={!userId}
                        onClick={() => onConfirm(userId)}
                        data-test="work-flow-actions.confirm-start-work-btn"
                    >
                        {confirmLabel ?? translate('workflow:START_WORK')}
                    </Button>
                </>
            }
        >
            <Box>
                <Box mb={2}>
                    {stepName && (
                        <Typography variant="body1" mb={2}>
                            {translate('workflow:CONFIRM_START_WORK', {
                                stepName,
                                interpolation: { escapeValue: false },
                            })}
                        </Typography>
                    )}
                    <Typography variant="subtitle2">{translate('common:TECHNICIAN')}</Typography>
                    <Select
                        fullWidth
                        value={userId}
                        onChange={(e: SelectChangeEvent) => setUserId(e.target.value)}
                        renderValue={(value) => {
                            const item = users.find(({ _id: v }) => v === value);
                            return item ? `${item.name.firstName} ${item.name.lastName}` : value;
                        }}
                        data-test="work-flow-actions.start-work-user-select"
                    >
                        {users.map((user, index) => (
                            <MenuItem value={user._id} key={index}>
                                {`${user.name.firstName} ${user.name.lastName}`}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default StartWorkDialog;
